import type { IFile, IFileUploadList } from '@ben/orion/file-upload-lists';
import { FileUploadList } from '@ben/orion/file-upload-lists';
import type { IFileUploader } from '@ben/orion/file-uploader';
import FileUploader from '@ben/orion/file-uploader';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import {
  deleteBenefitDocument,
  uploadBenefitDocument,
} from '@ben/shared/api/ea/benefits';
import {
  deleteEnrolmentDocument,
  uploadEnrolmentDocument,
} from '@ben/shared/api/ecc/enrolment-files';
import { IS_EA } from '@ben/shared/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';

interface IFileUploadControl extends IFileUploader, IFileUploadList {
  benefitId: number;
  enrollmentId?: string;
  id: number | string;
  fieldId?: number;
  maxFilesNumber: number;
  showLabel: boolean;
  label: string;
  onChange: (files: IFile[]) => void;
  value?: IFile[];
}

export const resolveFileUploadFieldId = (
  id: number | string,
  fieldId?: number
): number | undefined => {
  if (Number.isInteger(fieldId)) return fieldId;
  if (typeof id === 'number') return id;
  const [legacyFieldId] = id.split('-');
  const parsedFieldId = Number(legacyFieldId);
  return Number.isInteger(parsedFieldId) ? parsedFieldId : undefined;
};

export const FileUploadControl = (props: IFileUploadControl) => {
  const { t } = useTranslation();
  const {
    benefitId,
    enrollmentId,
    id,
    fieldId,
    maxFilesNumber,
    showLabel,
    label,
    onChange,
    value,
    maxFileSize,
  } = props;

  const resolvedFieldId = resolveFileUploadFieldId(id, fieldId);
  const resolvedEnrolmentId = enrollmentId ? Number(enrollmentId) : undefined;

  const handleUploadFiles = useCallback(
    (files: File[]) => {
      if (!resolvedFieldId)
        return Promise.reject(new Error('Invalid field id'));
      if (IS_EA) {
        return uploadBenefitDocument(files, benefitId, resolvedFieldId);
      }
      if (!resolvedEnrolmentId) {
        return Promise.reject(
          new Error('enrollmentId is required for ECC file uploads')
        );
      }
      return uploadEnrolmentDocument(
        files,
        resolvedEnrolmentId,
        resolvedFieldId
      );
    },
    [benefitId, resolvedEnrolmentId, resolvedFieldId]
  );

  const handleDeleteFile = useCallback(
    (uuid: string) => {
      if (!resolvedFieldId)
        return Promise.reject(new Error('Invalid field id'));
      if (IS_EA) {
        return deleteBenefitDocument(uuid, benefitId, resolvedFieldId);
      }
      if (!resolvedEnrolmentId) {
        return Promise.reject(
          new Error('enrollmentId is required for ECC file deletion')
        );
      }
      return deleteEnrolmentDocument(
        uuid,
        resolvedEnrolmentId,
        resolvedFieldId
      );
    },
    [benefitId, resolvedEnrolmentId, resolvedFieldId]
  );

  const uploadDocuments = useMutation(
    (files: File[]) => handleUploadFiles(files),
    {
      onSuccess: (data) => {
        onChange(value ? [...value, ...data] : data);
      },
    }
  );
  const deleteDocuments = useMutation(
    (uuid: string) => handleDeleteFile(uuid),
    {
      onSuccess: (_data, uuid) => {
        onChange(value?.filter((file) => file.uuid !== uuid) || []);
      },
      onError: (data: AxiosError) => {
        toast.error(
          Array.isArray(data?.response?.data)
            ? data.response.data[0]
            : t('documentUpload.errors.deleteError')
        );
      },
    }
  );
  const handleUpload = useCallback(
    (files: File[]) => {
      uploadDocuments.mutate(files);
    },
    [uploadDocuments]
  );
  const handleDelete = useCallback(
    (uuid: string) => {
      deleteDocuments.mutate(uuid);
    },
    [deleteDocuments]
  );

  return (
    <Container>
      {showLabel && label && (
        <Typography variant={TypographyVariant.bodyLg2}>{label}</Typography>
      )}
      <ComponentsContainer>
        {(!value || value?.length < maxFilesNumber) && (
          <FileUploader
            {...props}
            maxFileSize={maxFileSize ?? 2}
            maxFilesNumber={
              value && value.length > 0
                ? maxFilesNumber - value.length
                : maxFilesNumber
            }
            onFileUpload={handleUpload}
            uploading={uploadDocuments.isLoading}
            uploadError={
              uploadDocuments?.error
                ? t('documentUpload.errors.uploadError')
                : undefined
            }
          />
        )}
        {value && value?.length > 0 && (
          <ListContainer>
            <FileUploadList
              {...props}
              files={value}
              onRemove={handleDelete}
              buttonCopy={{
                remove: t('documentUpload.removeFile'),
                edit: t('documentUpload.editFile'),
              }}
            />
          </ListContainer>
        )}
      </ComponentsContainer>
    </Container>
  );
};

export default FileUploadControl;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex-direction: column;
  `}
`;
const ComponentsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(4)};
    flex-direction: column;
  `}
`;

const ListContainer = styled.ul`
  padding-left: 0;
  margin: 0;
`;
